import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { Button, Flex, Grid, Select, Spin, Table, Tag } from "antd"
import { CloseOutlined, PlusOutlined, LoadingOutlined, CalendarOutlined } from "@ant-design/icons"
import { Text } from "ui/Typography"
import MultiSelect from "components/common/MultiSelect"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  getAssignedCrew,
  getAvailableStaff,
  assignStaffMember,
  unassignStaffMember,
  selectAssignedCrew,
  selectAssignedCrewLoading,
  selectAssignedCrewLoaded,
  selectAvailableStaff,
  selectAvailableStaffLoading
} from "store/staffAssignments"
import { getRoles, selectBookingAssignableRoles } from "store/staff"
import { getCrewTimeOff, createCrewTimeOff, updateCrewTimeOff, deleteCrewTimeOff } from "store/crew-profiles"
import ScheduleTimeOffModal from "pages/guide/resources/crewMembers/crewDetail/components/ScheduledTimeOff/modal/ScheduleTimeOff"
import { ConfirmModal } from "ui/ConfirmModal"
import BookingConflictModal from "components/common/BookingConflictModal"
import { useBookingConflict } from "hooks/useBookingConflict"
import { toaster } from "components/common/Toast"
import styles from "./index.module.css"

const CrewTab = ({ booking, onAssignmentChange }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const [selectedRoles, setSelectedRoles] = useState([])
  const [availabilityFilter, setAvailabilityFilter] = useState("available")
  const availabilityFilterRef = useRef(availabilityFilter)
  availabilityFilterRef.current = availabilityFilter
  const [showAvailableCrew, setShowAvailableCrew] = useState(false)
  const [assigningStaffId, setAssigningStaffId] = useState(null)
  const [unassigningCrewId, setUnassigningCrewId] = useState(null)
  const [timeOffModalOpen, setTimeOffModalOpen] = useState(false)
  const [selectedCrewForTimeOff, setSelectedCrewForTimeOff] = useState(null)
  const [editingTimeOffRecord, setEditingTimeOffRecord] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState(null)
  const [currentAction, setCurrentAction] = useState(null)

  const { handleBookingConflictError, bookingConflictModalProps } = useBookingConflict()

  const assignedCrew = useSelector(selectAssignedCrew)
  const assignedCrewLoading = useSelector(selectAssignedCrewLoading)
  const assignedCrewLoaded = useSelector(selectAssignedCrewLoaded)
  const availableStaff = useSelector(selectAvailableStaff)
  const availableStaffLoading = useSelector(selectAvailableStaffLoading)
  const bookingRoles = useSelector(selectBookingAssignableRoles)

  const { data: timeOffData = [] } = useSelector((state) => state.crewProfiles.timeOff)
  const { loading: creating } = useSelector((state) => state.crewProfiles.createTimeOff)
  const { loading: updating } = useSelector((state) => state.crewProfiles.updateTimeOff)
  const { loading: deleting } = useSelector((state) => state.crewProfiles.deleteTimeOff)

  const bookingId = booking?.id
  const canEdit = booking?.can_edit

  useEffect(() => {
    if (bookingId) {
      dispatch(getAssignedCrew(bookingId))
    }
  }, [dispatch, bookingId])

  useEffect(() => {
    if (bookingId) {
      const params = { filter: availabilityFilter }
      dispatch(getAvailableStaff(bookingId, params))
    }
  }, [dispatch, bookingId, availabilityFilter])

  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  const experienceRoles = useMemo(() => {
    return bookingRoles?.map((role) => role?.name)
  }, [bookingRoles])

  const getStaffPrimaryRole = (staff) => {
    if (staff?.roles && staff?.roles?.length > 0) {
      return staff.roles[0].name
    }
    return "N/A"
  }

  const handleUnassign = useCallback(
    async (assignmentId) => {
      if (!assignmentId) return

      setUnassigningCrewId(assignmentId)
      try {
        await dispatch(unassignStaffMember(assignmentId))
        if (bookingId) {
          dispatch(getAssignedCrew(bookingId))
          dispatch(getAvailableStaff(bookingId, { filter: availabilityFilterRef.current }))
        }
        onAssignmentChange?.()
      } catch (error) {
        console.error("Failed to unassign crew member:", error)
      } finally {
        setUnassigningCrewId(null)
      }
    },
    [dispatch, bookingId, onAssignmentChange]
  )

  const handleAssign = useCallback(
    async (staffMember) => {
      if (!bookingId || !staffMember) return

      const primaryRole =
        staffMember.roles && staffMember.roles.length > 0 ? staffMember.roles[0].name.toLowerCase().replaceAll(/\s+/g, "_") : "crew_member"

      setAssigningStaffId(staffMember.id)
      try {
        await dispatch(
          assignStaffMember(bookingId, {
            profile_id: staffMember.id,
            role: primaryRole
          })
        )
        dispatch(getAssignedCrew(bookingId))
        dispatch(getAvailableStaff(bookingId, { filter: availabilityFilterRef.current }))
        onAssignmentChange?.()
      } catch (error) {
        console.error("Failed to assign crew member:", error)
      } finally {
        setAssigningStaffId(null)
      }
    },
    [dispatch, bookingId, onAssignmentChange]
  )

  const handleRoleFilter = (values) => {
    setSelectedRoles(values)
  }

  const fetchTimeOff = useCallback(
    (profileId) => {
      if (profileId) {
        dispatch(getCrewTimeOff(profileId, { time_frame: "upcoming", page: 1, per_page: 100 }))
      }
    },
    [dispatch]
  )

  const handleOpenTimeOff = useCallback(
    (crew) => {
      setSelectedCrewForTimeOff(crew)
      setEditingTimeOffRecord(null)
      setTimeOffModalOpen(true)
      fetchTimeOff(crew.profileId)
    },
    [fetchTimeOff]
  )

  const handleEditTimeOff = (record) => {
    setEditingTimeOffRecord(record)
    setTimeOffModalOpen(true)
  }

  const handleDeleteTimeOff = (record) => {
    setRecordToDelete(record)
    setDeleteConfirmOpen(true)
  }

  const confirmDeleteTimeOff = () => {
    const profileId = selectedCrewForTimeOff?.profileId
    if (recordToDelete && profileId) {
      dispatch(deleteCrewTimeOff(profileId, recordToDelete.id))
        .then(() => {
          toaster.success(t("crewMember.scheduledTimeOff.messages.deleteSuccess"))
          setDeleteConfirmOpen(false)
          setRecordToDelete(null)
          fetchTimeOff(profileId)
          dispatch(getAvailableStaff(bookingId, { filter: availabilityFilterRef.current }))
        })
        .catch(() => {
          setDeleteConfirmOpen(false)
          setRecordToDelete(null)
        })
    } else {
      setDeleteConfirmOpen(false)
      setRecordToDelete(null)
    }
  }

  const cancelDeleteTimeOff = () => {
    setDeleteConfirmOpen(false)
    setRecordToDelete(null)
  }

  const handleSaveTimeOff = (values) => {
    setCurrentAction("save")
    const profileId = selectedCrewForTimeOff?.profileId
    const action = editingTimeOffRecord
      ? updateCrewTimeOff(profileId, editingTimeOffRecord.id, values, true)
      : createCrewTimeOff(profileId, values, true)

    return dispatch(action)
      .then(() => {
        toaster.success(
          editingTimeOffRecord
            ? t("crewMember.scheduledTimeOff.messages.updateSuccess")
            : t("crewMember.scheduledTimeOff.messages.createSuccess")
        )
        setTimeOffModalOpen(false)
        setEditingTimeOffRecord(null)
        setCurrentAction(null)
        fetchTimeOff(profileId)
        dispatch(getAvailableStaff(bookingId, { filter: availabilityFilterRef.current }))
      })
      .catch((error) => {
        setCurrentAction(null)
        handleBookingConflictError(error)
      })
  }

  const handleSaveAndAddNewTimeOff = (values) => {
    setCurrentAction("saveAndAddNew")
    const profileId = selectedCrewForTimeOff?.profileId
    return dispatch(createCrewTimeOff(profileId, values, true))
      .then(() => {
        toaster.success(t("crewMember.scheduledTimeOff.messages.createSuccess"))
        setEditingTimeOffRecord(null)
        setCurrentAction(null)
        fetchTimeOff(profileId)
        dispatch(getAvailableStaff(bookingId, { filter: availabilityFilterRef.current }))
      })
      .catch((error) => {
        setCurrentAction(null)
        handleBookingConflictError(error)
      })
  }

  const transformedTimeOffData = useMemo(
    () =>
      timeOffData.map((item) => ({
        id: item.id,
        day: item.day_range,
        date: item.date_display,
        time: item.time_display,
        start_date: item.start_date,
        end_date: item.end_date,
        start_time: item.start_time,
        end_time: item.end_time,
        part_day: item.part_day,
        reason: item.reason,
        notes: item.notes
      })),
    [timeOffData]
  )

  const filteredAvailableCrew = useMemo(() => {
    if (selectedRoles.length === 0) return availableStaff

    return availableStaff.filter((staff) => {
      return staff.roles.some((role) => selectedRoles.includes(role.name))
    })
  }, [availableStaff, selectedRoles])

  const roleOptions = useMemo(() => {
    return experienceRoles?.map((roleName) => ({
      label: roleName,
      value: roleName
    }))
  }, [experienceRoles])

  const tableLoadingIndicator = <Spin spinning />

  const assignedCrewColumns = useMemo(() => {
    const cols = [
      {
        title: <Text className={styles.headerText}>{t("booking.crew.crew_member")}</Text>,
        dataIndex: "profileName",
        key: "profileName",
        width: "50%",
        render: (value) => <Text>{value || "-"}</Text>
      },
      {
        title: <Text className={styles.headerText}>{t("booking.crew.role")}</Text>,
        dataIndex: "assignmentRole",
        key: "assignmentRole",
        render: (value) => <Text>{value || "-"}</Text>
      }
    ]

    if (canEdit) {
      cols.push(
        {
          title: <Text className={styles.headerText}>{t("booking.crew.edit_time_off", { defaultValue: "Time Off" })}</Text>,
          key: "timeOff",
          width: 100,
          align: "center",
          render: (_, crew) => (
            <button className={styles.unassignButton} onClick={() => handleOpenTimeOff({ ...crew, profileId: crew.profileId })}>
              <CalendarOutlined />
            </button>
          )
        },
        {
          title: <Text className={styles.headerText}>{t("booking.crew.unassign")}</Text>,
          key: "action",
          width: 100,
          align: "center",
          render: (_, crew) => (
            <button className={styles.unassignButton} onClick={() => handleUnassign(crew.id)} disabled={unassigningCrewId === crew.id}>
              {unassigningCrewId === crew.id ? <LoadingOutlined style={{ fontSize: 16 }} spin /> : <CloseOutlined />}
            </button>
          )
        }
      )
    }

    return cols
  }, [canEdit, t, unassigningCrewId, handleUnassign, handleOpenTimeOff])

  const availableCrewColumns = useMemo(() => {
    const cols = [
      {
        title: <Text className={styles.headerText}>{t("booking.crew.crew_member")}</Text>,
        dataIndex: "fullName",
        key: "fullName",
        render: (value, crew) => (
          <div className={styles.crewNameCell}>
            <Text>{value || "-"}</Text>
            {crew.status === "unavailable" && <Tag color="red">{t("booking.crew.status_day_off", { defaultValue: "Day Off" })}</Tag>}
            {crew.status === "has_bookings" && (
              <Tag color="orange">{t("booking.crew.status_booking_conflict", { defaultValue: "Booking Conflict" })}</Tag>
            )}
          </div>
        )
      },
      {
        title: <Text className={styles.headerText}>{t("booking.crew.role")}</Text>,
        key: "role",
        render: (_, crew) => <Text>{getStaffPrimaryRole(crew)}</Text>
      }
    ]

    if (canEdit) {
      cols.push(
        {
          title: <Text className={styles.headerText}>{t("booking.crew.edit_time_off", { defaultValue: "Time Off" })}</Text>,
          key: "timeOff",
          width: 100,
          align: "center",
          render: (_, crew) => (
            <button className={styles.unassignButton} onClick={() => handleOpenTimeOff({ ...crew, profileId: crew.id })}>
              <CalendarOutlined />
            </button>
          )
        },
        {
          title: <Text className={styles.headerText}>{t("booking.crew.assign")}</Text>,
          key: "action",
          width: 100,
          align: "center",
          render: (_, crew) => (
            <button
              className={styles.assignButton}
              onClick={() => handleAssign(crew)}
              disabled={assigningStaffId === crew.id || crew.status === "has_bookings"}
            >
              {assigningStaffId === crew.id ? <LoadingOutlined style={{ fontSize: 16 }} spin /> : <PlusOutlined />}
            </button>
          )
        }
      )
    }

    return cols
  }, [canEdit, t, assigningStaffId, handleAssign, handleOpenTimeOff])

  const renderAssignedCrewContent = () => {
    if (isMobile) {
      if (assignedCrewLoading)
        return (
          <Flex justify="center" className={styles.mobileLoadingContainer}>
            <Spin spinning />
          </Flex>
        )
      if (!assignedCrew.length)
        return assignedCrewLoaded ? <Text className={styles.mobileEmptyText}>{t("booking.crew.no_assigned_crew")}</Text> : null
      return (
        <Flex vertical gap={8}>
          {assignedCrew.map((crew) => (
            <div key={crew.id} className={styles.mobileCard}>
              <Flex vertical gap={4} className={styles.mobileCardInfo}>
                <Text fw={500}>{crew.profileName || "-"}</Text>
                <Text className={styles.mobileCardRole}>{crew.assignmentRole || "-"}</Text>
              </Flex>
              {canEdit && (
                <button className={styles.unassignButton} onClick={() => handleUnassign(crew.id)} disabled={unassigningCrewId === crew.id}>
                  {unassigningCrewId === crew.id ? <Spin spinning /> : <CloseOutlined />}
                </button>
              )}
            </div>
          ))}
        </Flex>
      )
    }
    return (
      <Table
        columns={assignedCrewColumns}
        dataSource={assignedCrew}
        rowKey="id"
        pagination={false}
        loading={{ spinning: assignedCrewLoading, indicator: tableLoadingIndicator }}
        locale={{ emptyText: assignedCrewLoaded ? <Text>{t("booking.crew.no_assigned_crew")}</Text> : " " }}
        className={styles.crewTable}
      />
    )
  }

  const renderAvailableCrewContent = () => {
    if (isMobile) {
      if (availableStaffLoading)
        return (
          <Flex justify="center" className={styles.mobileLoadingContainer}>
            <Spin spinning />
          </Flex>
        )
      if (!filteredAvailableCrew.length) return <Text className={styles.mobileEmptyText}>{t("booking.crew.no_available_crew")}</Text>
      return (
        <Flex vertical gap={8}>
          {filteredAvailableCrew.map((crew) => (
            <div key={crew.id} className={styles.mobileCard}>
              <Flex vertical gap={4} className={styles.mobileCardInfo}>
                <Text fw={500}>{crew.fullName || "-"}</Text>
                <Text className={styles.mobileCardRole}>{getStaffPrimaryRole(crew)}</Text>
              </Flex>
              {canEdit && (
                <button className={styles.assignButton} onClick={() => handleAssign(crew)} disabled={assigningStaffId === crew.id}>
                  {assigningStaffId === crew.id ? <Spin spinning /> : <PlusOutlined />}
                </button>
              )}
            </div>
          ))}
        </Flex>
      )
    }
    return (
      <Table
        columns={availableCrewColumns}
        dataSource={filteredAvailableCrew}
        rowKey="id"
        pagination={false}
        loading={{ spinning: availableStaffLoading, indicator: tableLoadingIndicator }}
        locale={{ emptyText: <Text>{t("booking.crew.no_available_crew")}</Text> }}
        className={styles.crewTable}
      />
    )
  }

  return (
    <div className={styles.crewTabContainer}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>
            {t("booking.crew.assigned_crew")} ({assignedCrew.length})
          </Text>
        </div>
        <div className={styles.tableContainer}>{renderAssignedCrewContent()}</div>
        <Button type="link" icon={<PlusOutlined />} className={styles.addButton} onClick={() => setShowAvailableCrew(!showAvailableCrew)}>
          {t("booking.crew.add_crew_member")}
        </Button>
      </div>
      {showAvailableCrew && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>
              {t("booking.crew.available_crew")} ({filteredAvailableCrew.length})
            </Text>
          </div>
          <div className={styles.filterContainer}>
            <Select value={availabilityFilter} onChange={setAvailabilityFilter} style={{ minWidth: 140 }} disabled={availableStaffLoading}>
              <Select.Option value="available">{t("booking.crew.availability_available", { defaultValue: "Available" })}</Select.Option>
              <Select.Option value="all">{t("booking.crew.availability_all", { defaultValue: "All" })}</Select.Option>
            </Select>
            <MultiSelect
              placeholder={`${t("booking.crew.roles_placeholder")}`}
              value={selectedRoles}
              onChange={handleRoleFilter}
              options={roleOptions}
              style={{ minWidth: 200 }}
              disabled={availableStaffLoading || experienceRoles.length === 0}
            />

            {selectedRoles.length > 0 && (
              <div className={styles.roleTagsContainer}>
                {selectedRoles.map((role) => (
                  <Tag
                    key={role}
                    closable
                    onClose={() => setSelectedRoles(selectedRoles.filter((r) => r !== role))}
                    className={styles.roleTag}
                  >
                    {role}
                  </Tag>
                ))}
              </div>
            )}
          </div>
          <div className={styles.tableContainer}>{renderAvailableCrewContent()}</div>
        </div>
      )}

      <ScheduleTimeOffModal
        open={timeOffModalOpen}
        setIsModalOpen={setTimeOffModalOpen}
        onCancel={() => {
          setTimeOffModalOpen(false)
          setEditingTimeOffRecord(null)
          setCurrentAction(null)
        }}
        onSave={handleSaveTimeOff}
        onSaveAndAddNew={handleSaveAndAddNewTimeOff}
        onDeleteTimeOff={handleDeleteTimeOff}
        onEditTimeOff={handleEditTimeOff}
        editMode={!!editingTimeOffRecord}
        initialValues={editingTimeOffRecord}
        timeOffData={transformedTimeOffData}
        saveLoading={currentAction === "save" && (creating || updating)}
        saveAndAddNewLoading={currentAction === "saveAndAddNew" && creating}
        showExistingTimeOff
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title={t("crewMember.scheduledTimeOff.deleteConfirmation.title")}
        info={t("crewMember.scheduledTimeOff.deleteConfirmation.message")}
        onCancel={cancelDeleteTimeOff}
        onDelete={confirmDeleteTimeOff}
        deleteLabel={t("crewMember.scheduledTimeOff.table.actions.delete")}
        cancelLabel={t("crewMember.scheduledTimeOff.buttons.cancel")}
        loading={deleting}
      />

      <BookingConflictModal {...bookingConflictModalProps} />
    </div>
  )
}

CrewTab.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    can_edit: PropTypes.bool
  }),
  onAssignmentChange: PropTypes.func
}

export default CrewTab
